import React, { useState, useEffect, useCallback } from 'react';
import { Instructor } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';

const emptyFormData = {
  name: '',
  email: '',
  title: '',
  bio: '',
  discountCode: '',
};

const normalizeInstructor = (instructor: Partial<Instructor>): Instructor => ({
  id: instructor.id || '',
  name: instructor.name || '',
  email: instructor.email || '',
  title: instructor.title || '',
  bio: instructor.bio || '',
  photoUrl: instructor.photoUrl || '',
  discountCode: instructor.discountCode || '',
  entraObjectId: instructor.entraObjectId || '',
});

const hasInstructorChanges = (instructor: Instructor, formData: typeof emptyFormData) => {
  return (
    instructor.name !== formData.name ||
    instructor.email !== formData.email ||
    instructor.title !== formData.title ||
    instructor.bio !== formData.bio ||
    instructor.discountCode !== formData.discountCode
  );
};


const readErrorResponse = async (res: Response, fallbackMessage: string) => {
  const contentType = res.headers.get('content-type') || '';
  const debugRequestId = res.headers.get('x-debug-request-id');

  if (contentType.includes('application/json')) {
    const data = await res.json().catch(() => null);
    const message = data?.message || data?.error || fallbackMessage;
    const detail = data?.detail ? ` Details: ${data.detail}` : '';
    const requestId = data?.requestId || debugRequestId;
    const requestSuffix = requestId ? ` Reference ID: ${requestId}.` : '';
    return `${message}${detail}${requestSuffix}`.trim();
  }

  const bodyText = await res.text().catch(() => '');
  const emptyBodySuffix = bodyText ? ` Response: ${bodyText.slice(0, 200)}` : ' Empty response body.';
  const requestSuffix = debugRequestId ? ` Reference ID: ${debugRequestId}.` : '';
  return `${fallbackMessage} (${res.status} ${res.statusText}).${emptyBodySuffix}${requestSuffix}`.trim();
};

const ManageInstructors: React.FC = () => {
  usePageTitle('Manage Instructors');
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const fetchData = useCallback(async () => {
    try {
      setError('');

      const adminRes = await authFetch('/api/manage/instructors');
      if (adminRes.ok) {
        const adminInstructors = (await adminRes.json()) as Instructor[];
        setInstructors(adminInstructors.map(normalizeInstructor));
        return;
      }

      if (adminRes.status === 404) {
        const publicRes = await authFetch('/api/instructors');
        if (publicRes.ok) {
          const publicInstructors = (await publicRes.json()) as Instructor[];
          setInstructors(publicInstructors.map(normalizeInstructor));
          return;
        }
      }

      const errorData = await adminRes.json().catch(() => null);
      setError(errorData?.message || 'Failed to load instructors.');
      setInstructors([]);
    } catch (err) {
      console.error('Error fetching instructors:', err);
      setError('Failed to load instructors.');
      setInstructors([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (instructor: Instructor) => {
    setEditing(instructor);
    setCreating(false);
    setFormData({
      name: instructor.name,
      email: instructor.email,
      title: instructor.title,
      bio: instructor.bio,
      discountCode: instructor.discountCode,
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
  };

  const handleCreate = () => {
    setCreating(true);
    setEditing(null);
    setFormData({ ...emptyFormData });
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setPhotoFile(null);
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
    setError('');
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG or PNG).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB.');
      return;
    }

    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.title || !formData.bio) {
      setError('Name, email, title, and bio are required.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const shouldSaveInstructor = !editing || hasInstructorChanges(editing, formData);
      let savedInstructor = editing;

      if (shouldSaveInstructor) {
        const url = editing ? `/api/manage/instructors/${editing.id}` : '/api/manage/instructors';
        const method = editing ? 'PUT' : 'POST';
        const res = await authFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) {
          const message = await readErrorResponse(res, 'Failed to save instructor');
          setError(message);
          setSaving(false);
          return;
        }

        savedInstructor = await res.json();
      }

      // Upload photo if one was selected
      if (photoFile) {
        const instructorId = editing ? editing.id : savedInstructor?.id;

        if (!instructorId) {
          setError('Instructor saved but photo upload could not determine the instructor ID.');
          setSaving(false);
          return;
        }
        const photoRes = await authFetch(`/api/manage/instructors/${instructorId}/photo`, {
          method: 'POST',
          headers: { 'Content-Type': photoFile.type },
          body: photoFile,
        });
        if (!photoRes.ok) {
          const message = await readErrorResponse(photoRes, 'Instructor saved but photo upload failed.');
          setError(message);
          await fetchData();
          handleCancel();
          setSaving(false);
          return;
        }
      }

      await fetchData();
      handleCancel();
    } catch {
      setError('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (instructorId: string) => {
    if (!window.confirm('Are you sure you want to delete this instructor? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/manage/instructors/${instructorId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchData();
      } else {
        const message = await readErrorResponse(res, 'Failed to delete instructor');
        alert(message);
      }
    } catch (err) {
      console.error('Error deleting instructor:', err);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading instructors...</p>
      </div>
    );
  }

  const showForm = creating || editing !== null;

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <div className="section-header-row">
            <div>
              <h1 className="page-title">Manage Instructors</h1>
              <p className="page-subtitle">{instructors.length} instructor{instructors.length !== 1 ? 's' : ''}</p>
            </div>
            {!showForm && (
              <button className="btn btn-primary" onClick={handleCreate}>
                + Add Instructor
              </button>
            )}
          </div>
        </div>
      </section>

      {error && !showForm && (
        <section className="section">
          <div className="section-container">
            <div className="form-error">{error}</div>
          </div>
        </section>
      )}

      {showForm && (
        <section className="section">
          <div className="section-container">
            <div className="admin-form">
              <h2 className="section-title">{editing ? 'Edit Instructor' : 'New Instructor'}</h2>

              {error && <div className="form-error" style={{ marginBottom: '16px' }}>{error}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full Name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    className="form-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    className="form-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Senior Security Consultant"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Discount Code</label>
                  <input
                    className="form-input"
                    type="text"
                    value={formData.discountCode}
                    onChange={(e) => setFormData({ ...formData, discountCode: e.target.value })}
                    placeholder="e.g., JohnDoe"
                  />
                </div>

                <div className="form-group form-full">
                  <label className="form-label">Bio *</label>
                  <textarea
                    className="form-textarea"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={4}
                    placeholder="Instructor biography..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Photo</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoChange}
                    className="form-input"
                    style={{ padding: '8px' }}
                  />
                  {(photoPreview || (editing && editing.photoUrl)) && (
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={photoPreview || editing?.photoUrl}
                        alt="Preview"
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid var(--gray-200)',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-outline" onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Instructor' : 'Create Instructor'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-container">
          {instructors.length > 0 ? (
            <div className="instructor-manage-grid">
              {instructors.map((instructor) => (
                <div key={instructor.id} className="instructor-manage-card">
                  {/* Photo / Avatar */}
                  <div style={{ marginBottom: '12px' }}>
                    {instructor.photoUrl ? (
                      <img
                        src={instructor.photoUrl}
                        alt={instructor.name}
                        style={{
                          width: '96px',
                          height: '96px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '3px solid var(--gray-200)',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--navy), var(--blue))',
                        color: 'white',
                        display: instructor.photoUrl ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                      }}
                    >
                      {getInitials(instructor.name)}
                    </div>
                  </div>

                  {/* Name */}
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: 'var(--navy)',
                    margin: '0 0 2px 0',
                  }}>
                    {instructor.name}
                  </h3>

                  {/* Title */}
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--gray-600)',
                    margin: '0 0 12px 0',
                  }}>
                    {instructor.title}
                  </p>

                  {/* Discount Code Badge */}
                  {instructor.discountCode ? (
                    <span style={{
                      display: 'inline-block',
                      padding: '5px 14px',
                      borderRadius: '9999px',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      letterSpacing: '1px',
                      color: 'var(--blue)',
                      background: 'rgba(37, 99, 235, 0.08)',
                      border: '1px solid rgba(37, 99, 235, 0.14)',
                      marginBottom: '12px',
                    }}>
                      {instructor.discountCode}
                    </span>
                  ) : null}

                  {/* Email */}
                  <p style={{
                    fontSize: '0.8125rem',
                    color: 'var(--gray-500)',
                    margin: '0 0 12px 0',
                  }}>
                    {instructor.email}
                  </p>

                  {/* Bio (truncated) */}
                  {instructor.bio && (
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--gray-600)',
                      lineHeight: 1.6,
                      margin: '0 0 16px 0',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                    }}>
                      {instructor.bio}
                    </p>
                  )}

                  {/* Spacer */}
                  <div style={{ flex: 1 }} />

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    paddingTop: '16px',
                    borderTop: '1px solid var(--gray-200)',
                    width: '100%',
                    justifyContent: 'center',
                  }}>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleEdit(instructor)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                      onClick={() => handleDelete(instructor.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">No instructors found.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManageInstructors;
